// OLMPS home — ONE spatial canvas, no lens tabs.
//
//                     [ SELF ]         Deities at the top of the mountain; climbing up shows who you hold
//                         ▼▲
//  [ DASHBOARD ] ◀▶ [ COMPASS ] ▶ [ NUMA ]   Home lands on the dashboard; compass pinned on the peak
//             ▼▲
//        [ ORB = NOW ] ▶ [ TOOL ]   opens here; the orb hangs from the compass
//
// Every screen is a full screen; each move is a full-screen pan (arrows or
// swipes) of one big board, and the backdrop descends from mountain sky into
// deep-navy starfield so it reads as one world. The orb IS the NOW screen.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Image, PanResponder, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BlurView } from 'expo-blur';
import { LinearGradient } from 'expo-linear-gradient';
import { Menu } from 'lucide-react-native';
import { QuickLinkButtons } from '../olmps/quickLinks';
import type { GearId } from '../olmps/ascension';
import type { UseTodayData } from '../olmps/useTodayData';
import type { Gear, Sector } from '../domain/olmps';
import { ORB_ROW_TOOL, type OrbCtx } from '../olmps/CompassView';
import type { OrbLevel, ToolOpts } from './orb/types';
import { EdgeTabs, NAV_D } from './EdgeTabs';
import { OnboardingProvider, TourLayer, tourBus } from './onboarding';
import { ToolScreen } from './ToolScreen';
import { readNav, readNavSync, writeNav } from '../lib/navPersist';
import { useCurrentStep, type NowStep } from './useCurrentStep';
import { useSelf } from './useSelf';
import { goalTextOf } from '../olmps/atlas-data';
import { effectiveAims } from '../olmps/oracle-spine-data';
import { NowOrb, timeGear } from './NowView';
import { LifeView, PEAK_FRAC, type LifeMode } from './LifeView';
import { SelfView } from './SelfView';
import { DeitySheet } from './DeitySheet';
import { toggleTalk } from './TalkingOverlay';
import { SectorDeityButton, SectorDeityStage, useSectorDeity } from './SectorDeity';
import { SectorOverview } from './SectorOverview';
import { DashboardView } from './DashboardView';
import { setScreen } from '../live/screenContext';
import { getDeityAvatar } from '../olmps/deityAvatars';
import { TOOL_NAMES } from '../domain/olmps';
import { TEMPLE_HALL, SEAT_BY_ID } from './roster';
import { makeStyles, useV10 } from './theme';
import { MountainBackdrop, pitchFrameIndex } from './MountainBackdrop';
import { ORBIT_FRAMES } from './orbitFrames';
import { AERIAL_PITCH, NightBackdrop, DayBackdrop, PITCH_STEP, SIDE_PITCH, SECTORS, useOrbit } from './orbit';
import { FlowRibbon, straight } from './FlowLine';
import { takePendingOrb, takePendingPantheon } from '../live/appActions';

export type CanvasPos = 'dashboard' | 'compass' | 'orb' | 'self' | 'tool' | 'deitytool' | 'numa';
/** Grid cell (col,row) of each screen on the board. */
// deitytool sits directly right of SELF: a deity's ▶ slides straight across to its tool.
// numa sits directly right of the compass: the compass ▶ opens NUMA (Joel, 2026-10-17).
// dashboard sits directly left of the compass: Home lands there, its ▶ opens the compass (Joel, 2026-10-18).
const GRID: Record<CanvasPos, [number, number]> = { dashboard: [0, 1], self: [1, 0], compass: [1, 1], orb: [1, 2], tool: [2, 2], deitytool: [2, 0], numa: [2, 1] };
const COLS = 3;
/** Saved positions a reload may land on (transient panels fall back elsewhere). */
const RESTORABLE: CanvasPos[] = ['dashboard', 'self', 'compass', 'numa'];
const posAt = (c: number, r: number) => (Object.keys(GRID) as CanvasPos[]).find((k) => GRID[k][0] === c && GRID[k][1] === r) ?? null;
type SavedCanvas = { pos: CanvasPos; tool: string | null };
const PAN_MS = 520;

export interface V10HomeProps {
  /** Increments when the drawer's Home is tapped → pan to the dashboard. */
  homeTick?: number;
  userId: string | undefined;
  today: UseTodayData;
  onOpenTool: (toolId: string, gear?: GearId, opts?: { initialTaskId?: string }) => void;
  onOpenDrawer: () => void;
  onCapture: () => void;
  onOpenSettings: () => void;
  onOpenAstra: () => void;
  /** Open the morning / evening session (the orb's O and final R rows). */
  onOpenSession?: (kind: 'morning' | 'night') => void;
}

export function V10Home({ userId, today, onOpenTool, onOpenDrawer, onCapture, onOpenSettings, onOpenAstra, onOpenSession, homeTick }: V10HomeProps) {
  const t = useV10();
  const styles = useStyles();
  const insets = useSafeAreaInsets();
  const { width: winW, height: winH } = useWindowDimensions();

  const saved = useRef(readNavSync<SavedCanvas>('v10canvas')).current;
  const [pos, setPosState] = useState<CanvasPos>(saved && (RESTORABLE.includes(saved.pos) || (saved.pos === 'tool' && saved.tool)) ? saved.pos : 'orb');
  const [lifeMode, setLifeModeState] = useState<LifeMode>(() => readNavSync<LifeMode>('v10life') ?? 'compass');
  const setLifeMode = (m: LifeMode) => {
    setLifeModeState(m);
    writeNav('v10life', m);
  };
  const [stepIdx, setStepIdx] = useState(0);
  const [deityId, setDeityId] = useState<string | null>(null);
  const [orbCtx, setOrbCtx] = useState<OrbCtx>({ spokeLabel: '', sector: null, stage: 'S', stageLabel: 'Step', rungState: '' });
  // Which orb is showing: Step (S) when the app opens; the compass / mountain pick (T, A, …) after that.
  const [orbLevel, setOrbLevel] = useState<OrbLevel>('S');
  const [orbTarget, setOrbTarget] = useState<{ tool: string; opts?: ToolOpts } | null>(null);
  const swipeRef = useRef<(() => void) | null>(null);
  const mountedAt = useRef(Date.now());
  const lastStage = useRef<OrbCtx['stage'] | null>(null);
  const adoptNext = useRef(false);
  const orbCtxRef = useRef(orbCtx);
  // The compass and mountain both report their own stage as they mount; only a
  // change after that (or an explicit pick / Explore) switches the orb.
  // An orb request from another screen (TKNE's compass) holds its sector/stage
  // briefly so the home compass's own mount-time report doesn't overwrite it.
  const orbHoldUntil = useRef(0);
  const handleOrbCtx = (ctx: OrbCtx) => {
    if (Date.now() < orbHoldUntil.current) return;
    orbCtxRef.current = ctx;
    setOrbCtx(ctx);
    const first = lastStage.current == null;
    const changed = ctx.stage !== lastStage.current;
    lastStage.current = ctx.stage;
    if (adoptNext.current) {
      adoptNext.current = false;
      setOrbLevel(ctx.stage);
      return;
    }
    if (first || !changed || Date.now() - mountedAt.current < 2500) return;
    setOrbLevel(ctx.stage);
  };

  // Orb row state: which row is open, which row's tool the ▶ screen shows.
  // Live clock: re-evaluated every minute so the default letter moves with the day.
  const [clock, setClock] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 60000);
    return () => clearInterval(id);
  }, []);
  const now = clock;
  const dayGear = timeGear(now);
  const [selOverride, setSelOverride] = useState<GearId | null>(null);
  const selectedGear = selOverride ?? dayGear;
  const [stepTool, setStepTool] = useState<{ id: string; opts?: ToolOpts } | null>(
    saved?.tool && saved.pos === 'tool' ? { id: saved.tool } : null,
  );
  const toolId = stepTool?.id ?? orbTarget?.tool ?? ORB_ROW_TOOL[selectedGear];
  const [toolMounted, setToolMounted] = useState(pos === 'tool');
  const [numaMounted, setNumaMounted] = useState(pos === 'numa');
  const [dashMounted, setDashMounted] = useState(pos === 'dashboard');
  /** The compass ▶ opens NUMA; its ▲▼ can flip to the neighbouring tools. */
  const [numaTool, setNumaTool] = useState('numa');
  // The tool opened from a deity in the Pantheon (its own panel, right of SELF).
  const [deityTool, setDeityTool] = useState<{ id: string; opts?: ToolOpts; name: string } | null>(null);

  const { steps, pace, loading, projects, projectsAreDemo, spine, refresh } = useCurrentStep(userId, today);
  const self = useSelf(userId);
  const step: NowStep | null = steps[stepIdx] ?? steps[0] ?? null;
  const orbit = useOrbit(step?.sector ?? null);
  const orbitRef = useRef(orbit);
  orbitRef.current = orbit;
  // Camera pitch over the Mountain (Joel, 2026-10-12): Compass looks straight
  // down on the summit, Mountain is the side view, and the compass ▲▼ pitch the
  // whole world (mountain + disc together) between the two.
  const pitch = useRef(new Animated.Value(lifeMode === 'compass' ? AERIAL_PITCH : SIDE_PITCH)).current;
  const pitchTarget = useRef(lifeMode === 'compass' ? AERIAL_PITCH : SIDE_PITCH);
  const pitchTo = (v: number) => {
    pitchTarget.current = v;
    Animated.timing(pitch, { toValue: v, duration: 1100, easing: Easing.inOut(Easing.cubic), useNativeDriver: false }).start();
  };
  // 1 = compass (mountain lowered to the centred disc), 0 = the Mountain view's own placement.
  const lift = useRef(new Animated.Value(lifeMode === 'compass' ? 1 : 0)).current;
  // The disc moves to the Mountain's peak only once it has faded out, so it never jumps while visible.
  const [discAt, setDiscAt] = useState<LifeMode>(lifeMode);
  useEffect(() => {
    pitchTo(lifeMode === 'compass' ? AERIAL_PITCH : SIDE_PITCH);
    Animated.timing(lift, { toValue: lifeMode === 'compass' ? 1 : 0, duration: 1100, easing: Easing.inOut(Easing.cubic), useNativeDriver: false }).start();
    if (lifeMode === 'compass') {
      setDiscAt('compass');
      return;
    }
    const id = setTimeout(() => setDiscAt('mountain'), 450);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lifeMode]);
  // Straight down on the summit; going up from the side view first turns the mountain to its front face.
  const pitchFlat = () => {
    if (pitchFrameIndex(pitchTarget.current) === 0 && orbitRef.current.frame !== 0) orbitRef.current.snapToFace(0);
    pitchTo(AERIAL_PITCH);
  };
  const world = useMemo(
    () => ({
      pitch,
      onPitch: (dir: 1 | -1) => {
        // The camera rise was filmed from the front face: going up from the side view
        // first turns the mountain to that face, then the camera climbs.
        if (dir === 1 && pitchFrameIndex(pitchTarget.current) === 0 && orbitRef.current.frame !== 0) orbitRef.current.snapToFace(0);
        pitchTo(Math.min(AERIAL_PITCH, Math.max(SIDE_PITCH, +(pitchTarget.current + dir * PITCH_STEP).toFixed(3))));
      },
      // Triple ▲ / ▼ on the compass D-pad (Joel, 2026-10-18): ▲▲▲ jumps to the flat
      // top-down view, ▼▼▼ drops to the Mountain's side view.
      onPitchEnd: (dir: 1 | -1) => {
        if (dir === -1) return setLifeMode('mountain');
        pitchFlat();
      },
      // TKNE on: tilt down to the low view so the project stacks hang in sight; off: back to flat.
      onShowProjects: () => pitchTo(SIDE_PITCH),
      onHideProjects: pitchFlat,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pitch]
  );
  useEffect(() => {
    if (stepIdx >= steps.length && steps.length) setStepIdx(0);
  }, [steps.length, stepIdx]);

  const goCompassRef = useRef<() => void>(() => undefined);
  /** Sector the compass turns to 6 o'clock while its deity speaks in Play all. */
  const [compassSpin, setCompassSpin] = useState<Sector | null>(null);
  // The SHAPE deity of the active sector (compass pick / mountain face): top-bar face + lower-left stage.
  const sectorDeity = useSectorDeity({
    userId,
    sector: orbCtx.sector ?? step?.sector ?? 'S',
    spine,
    projects,
    projectsAreDemo,
    dayEntries: today.dayEntries,
    visible: pos === 'compass',
    onShowCompass: () => goCompassRef.current(),
    onSector: (s) => {
      if (lifeMode === 'mountain') orbit.snapToFace(SECTORS.indexOf(s));
      else setCompassSpin(s);
    },
  });

  // ---- layout (all in canvas-panel coordinates) -----------------------------
  const headerH = insets.top + 52;
  const pillTop = headerH + 2;
  // Mountain frame: at least screen-wide and big enough that the mountain
  // fills most of the screen below the compass (Joel, 2026-10-10: bigger,
  // taller). The peak (PEAK_FRAC down the frame) stays where the compass is,
  // so growing the frame only extends the mountain downward and outward; the
  // compass keeps its own width cap (CompassView STAGE_W).
  const frameSize = Math.round(Math.max(winW, winH * 0.82));
  // Mountain view: the peak sits where it always has (that layout is settled).
  const peakY = Math.round(Math.max(headerH + 150, winH * 0.32));
  // Compass view: the disc sits midway between the ▲ and ▼ circles so it never
  // rides up over the ▲; the mountain glides down under it (Joel, 2026-10-15).
  const compassY = Math.round((headerH + winH - insets.bottom) / 2);
  const anchorY = lifeMode === 'compass' ? compassY : peakY;
  const frameTop = Math.round(peakY - frameSize * PEAK_FRAC);
  const frameLeft = Math.round((winW - frameSize) / 2);
  const bottomY = winH;
  // The orb: as wide as the screen allows, with a side margin so it never touches
  // the edge — on wider screens the margin also clears the ◀ ▶ circles (6 + NAV_D
  // + 8). Vertically it leaves room for the ▲ ▼ circles and the ◐/☰ toggle below
  // it (Joel, 2026-10-12: bigger, but not touching the sides).
  const orbMargin = winW >= 600 ? 6 + NAV_D + 8 : Math.max(16, winW * 0.04);
  const orbSize = Math.round(Math.max(220, Math.min(winW - 2 * orbMargin, winH - headerH - insets.bottom - 112)));
  // The orb sits at the centre of the page area, so its ribbons run straight
  // out to the four direction circles.
  const orbCy = Math.round((headerH + winH - insets.bottom) / 2);
  const orbTop = orbCy - Math.round(orbSize / 2);
  const navC = 6 + NAV_D / 2;

  // ---- the canvas -----------------------------------------------------------
  const posRef = useRef(pos);
  const dims = useRef({ w: winW, h: winH, headerH });
  dims.current = { w: winW, h: winH, headerH };
  const modeRef = useRef(lifeMode);
  modeRef.current = lifeMode;
  const toolIdRef = useRef(toolId);
  toolIdRef.current = toolId;
  const tx = useRef(new Animated.Value(-GRID[pos][0] * winW)).current;
  const ty = useRef(new Animated.Value(-GRID[pos][1] * winH)).current;
  // Keep the resting offset right if the window is resized.
  useEffect(() => {
    tx.setValue(-GRID[posRef.current][0] * winW);
    ty.setValue(-GRID[posRef.current][1] * winH);
  }, [winW, winH, tx, ty]);

  // Mountain ↔ Pantheon: zoom into the peak temple, then fade up. k runs 0
  // (compass) → 1 (self) straight off the canvas offset, so the same 520ms pan,
  // a finger drag and the reverse swipe all drive it. The mountain zooms into
  // the temple, then drops away and fades as you rise; the hall fades in from
  // the bottom up where it sits, under a blur haze. (Tuned with Joel 2026-10-12.)
  const k = ty.interpolate({ inputRange: [-winH, 0], outputRange: [0, 1], extrapolate: 'clamp' });
  // Pinning/zooming only applies MID-flight: at rest (k or j exactly 0/1) every
  // panel snaps back to its own grid cell (while invisible), because on web an
  // invisible panel pinned over the current screen still swallows taps even
  // with pointerEvents="none" (its Pressables/TextInputs force pointer-events: auto).
  // Mountain ↔ orb (NOW) is the same fade effect mirrored for going DOWN, without the zoom. j runs 0
  // (compass) → 1 (orb) off the same canvas offset. k and j are never both
  // non-zero, so the mountain's two sets of transforms simply add/multiply.
  const j = ty.interpolate({ inputRange: [-2 * winH, -winH], outputRange: [1, 0], extrapolate: 'clamp' });
  const flyMountain = {
    opacity: Animated.multiply(
      k.interpolate({ inputRange: [0, 0.4, 0.8], outputRange: [1, 1, 0] }),
      j.interpolate({ inputRange: [0, 0.4, 0.8], outputRange: [1, 1, 0] }),
    ),
    // Zooms into the compass pin / temple point.
    transformOrigin: `${winW / 2}px ${anchorY}px`,
    transform: [
      // Held for the zoom, then falls away beneath you going up (k) or rises away above you going down (j).
      {
        translateY: Animated.add(
          k.interpolate({ inputRange: [0, 0.5, 0.999, 1], outputRange: [0, -winH * 0.325, winH * 0.3, 0] }),
          j.interpolate({ inputRange: [0, 0.999, 1], outputRange: [0, winH * 0.7, 0] }),
        ),
      },
      // Zoom only going up into the temple; going down to the orb is not a zoom.
      { scale: k.interpolate({ inputRange: [0, 0.5, 0.999, 1], outputRange: [1, 2.35, 3, 1] }) },
    ],
  };
  // Going down: the orb screen is pinned and fades in, revealed bottom first by
  // a sky-coloured veil sliding up and off it (mirror of the Pantheon's veil).
  const flyOrb = {
    opacity: j.interpolate({ inputRange: [0, 0.45, 0.7, 1], outputRange: [0, 0, 1, 1] }),
    transform: [{ translateY: j.interpolate({ inputRange: [0, 0.001, 1], outputRange: [0, -winH * 0.999, 0] }) }],
  };
  const orbVeil = {
    transform: [{ translateY: j.interpolate({ inputRange: [0, 0.45, 1], outputRange: [0, 0, -winH * 1.5], extrapolate: 'clamp' }) }],
  };
  // The mountain's bottom edge rises across the screen going down; feather it into the night.
  const mountainFeatherBottom = { opacity: j.interpolate({ inputRange: [0, 0.3], outputRange: [0, 1], extrapolate: 'clamp' }) };
  const flyHall = {
    // Pinned exactly (cancels the pan) so its edges never cross the screen as a
    // seam; the rising comes from the veil's edge sweeping UP the room below.
    opacity: k.interpolate({ inputRange: [0, 0.45, 0.7, 1], outputRange: [0, 0, 1, 1] }),
    transform: [{ translateY: k.interpolate({ inputRange: [0, 0.001, 1], outputRange: [0, winH * 0.999, 0] }) }],
  };
  // While the mountain falls away its top edge would cross the screen as a hard
  // line; a sky-coloured feather over that edge melts it into the night. Off at rest.
  const mountainFeather = { opacity: k.interpolate({ inputRange: [0, 0.3], outputRange: [0, 1], extrapolate: 'clamp' }) };
  // Fade-in from the top: a sky-coloured veil (soft edge on top, solid below)
  // over the hall slides down and off it, so the room appears top first and
  // its bottom last.
  const hallVeil = {
    transform: [{ translateY: k.interpolate({ inputRange: [0, 0.45, 1], outputRange: [0, 0, winH * 1.5], extrapolate: 'clamp' }) }],
  };
  // Blur runs the whole flight: in almost at once, held through the middle, clear only on landing.
  // Lighter mid-flight (0.55) so the climb's downward rush stays visible through it.
  const haze = Animated.add(
    k.interpolate({ inputRange: [0, 0.1, 0.3, 0.75, 0.92, 1], outputRange: [0, 0.45, 0.55, 0.55, 0.4, 0] }),
    j.interpolate({ inputRange: [0, 0.1, 0.3, 0.75, 0.92, 1], outputRange: [0, 0.45, 0.55, 0.55, 0.4, 0] }),
  );

  // Web: the clipped root — and each clipped screen panel (nativeID "v10-…") — can still be scrolled by
  // focus/scrollIntoView, which shifts the whole screen sideways (seen on the compass after a menu tap)
  // and breaks the tour's spotlight. Pin their scroll to zero; real ScrollViews inside carry no v10- id.
  const rootClipRef = useRef<View>(null);
  useEffect(() => {
    const el = rootClipRef.current as unknown as HTMLElement | null;
    if (!el || typeof el.addEventListener !== 'function') return;
    const pin = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target || (target !== el && !target.id?.startsWith('v10-'))) return;
      if (target.scrollTop || target.scrollLeft) { target.scrollTop = 0; target.scrollLeft = 0; }
    };
    // Capture: scroll events don't bubble, so this sees the panels' own scrolls too.
    el.addEventListener('scroll', pin, true);
    return () => el.removeEventListener('scroll', pin, true);
  }, []);
  const flightId = useRef(0);
  const inFlight = useRef(false);
  const pendingTourScreen = useRef<CanvasPos | null>(null);
  const goTo = (p: CanvasPos, silent = false) => {
    if (!silent) {
      tourBus.emit(`nav:${p}`);
      pendingTourScreen.current = null; // a user move supersedes any queued tour move
    }
    posRef.current = p;
    setPosState(p);
    if (p === 'tool') setToolMounted(true);
    if (p === 'numa') setNumaMounted(true);
    if (p === 'dashboard') setDashMounted(true);
    // The deity's tool panel is transient: a reload lands back on SELF.
    writeNav('v10canvas', { pos: p === 'deitytool' ? 'self' : p, tool: p === 'tool' ? toolIdRef.current : null } satisfies SavedCanvas);
    const cfg = { duration: PAN_MS, easing: Easing.inOut(Easing.cubic), useNativeDriver: true };
    const myFlight = ++flightId.current;
    inFlight.current = true;
    Animated.parallel([
      Animated.timing(tx, { toValue: -GRID[p][0] * dims.current.w, ...cfg }),
      Animated.timing(ty, { toValue: -GRID[p][1] * dims.current.h, ...cfg }),
    ]).start(({ finished }) => {
      if (finished && posRef.current !== 'tool') setToolMounted(false);
      if (finished && posRef.current !== 'deitytool') setDeityTool(null);
      if (myFlight !== flightId.current) return; // a newer pan took over
      inFlight.current = false;
      // Onboarding asked for a screen mid-flight: honour it only if we still aren't there.
      const want = pendingTourScreen.current;
      pendingTourScreen.current = null;
      if (finished && want && posRef.current !== want) goRef.current(want, true);
    });
  };
  // Drawer "Home" → the dashboard.
  useEffect(() => {
    if (!homeTick) return;
    // A voice agent asked for the Pantheon ("show me the deities"): home first, then pan up to SELF.
    if (takePendingPantheon()) return goTo('self');
    const orbReq = takePendingOrb();
    if (orbReq) {
      orbHoldUntil.current = Date.now() + 3000;
      const ctx: OrbCtx = { ...orbCtxRef.current, sector: orbReq.sector, stage: orbReq.stage };
      orbCtxRef.current = ctx;
      setOrbCtx(ctx);
      setOrbLevel(orbReq.stage);
      return goTo('orb');
    }
    goTo('dashboard');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeTick]);
  const goRef = useRef(goTo);
  goRef.current = goTo;
  goCompassRef.current = () => goTo('compass');
  useEffect(() => {
    if (!sectorDeity.playing) setCompassSpin(null);
  }, [sectorDeity.playing]);
  // Onboarding: only brand-new accounts (no oracle_spine Endeavours, no projects) auto-start.
  const tourEligible =
    !loading &&
    !!spine &&
    (Object.keys(spine) as Sector[]).every((s) => !spine[s]?.endeavour) &&
    (Object.values(projects) as unknown[][]).every((l) => !l?.length);
  // Onboarding asks for a screen; move only if we are not already there (silent: no nav event).
  const tourNavigate = (screen: 'compass' | 'self' | 'orb' | 'tool' | 'deitytool') => {
    if (screen !== 'compass' && screen !== 'self' && screen !== 'orb') return;
    if (inFlight.current) {
      pendingTourScreen.current = screen; // never start a pan on top of one in flight
      return;
    }
    if (posRef.current !== screen) goRef.current(screen, true);
  };

  // Native restores the saved position once storage has loaded.
  useEffect(() => {
    if (saved) return;
    void readNav<SavedCanvas>('v10canvas').then((s) => {
      if (!s || s.pos === 'orb') return;
      if (s.pos === 'tool' && s.tool) setStepTool({ id: s.tool });
      if (RESTORABLE.includes(s.pos) || (s.pos === 'tool' && s.tool)) goRef.current(s.pos);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Swipes: ▲ (up) from the orb → SELF, ◀ (left) → tool; the reverse brings
  // you home. SELF/tool only give up their own scrolling at their edge/top.
  const axis = useRef<'x' | 'y' | null>(null);
  /** Neighbour screen reached by dragging by (dx,dy) from `p`, if any. */
  const neighbour = (p: CanvasPos, a: 'x' | 'y', d: number): CanvasPos | null => {
    const [c, r] = GRID[p];
    // Dragging up/left reveals the screen below/right.
    return a === 'y' ? posAt(c, r + (d < 0 ? 1 : -1)) : posAt(c + (d < 0 ? 1 : -1), r);
  };
  const pan = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_e, g) => {
        const p = posRef.current;
        const ay = Math.abs(g.dy);
        const ax = Math.abs(g.dx);
        const a: 'x' | 'y' | null = ay > 22 && ay > ax * 1.5 ? 'y' : ax > 22 && ax > ay * 1.5 ? 'x' : null;
        if (!a) return false;
        const d = a === 'y' ? g.dy : g.dx;
        if (!neighbour(p, a, d)) return false;
        // Mountain mode: horizontal drags turn the mountain.
        if (p === 'compass' && a === 'x') return false;
        // SELF scrolls: only the bottom strip hands over an upward drag. Tools: left edge only.
        if (p === 'self' && !(g.y0 > dims.current.h - 160)) return false;
        // SELF's sideways drags turn the rotunda; its tool panel is reached by a deity's ▶ only.
        if (p === 'self' && a === 'x') return false;
        if ((p === 'tool' || p === 'deitytool' || p === 'numa') && !(g.x0 < 40)) return false;
        // Tool screens only swipe back sideways (no falling into the tool stacked above/below).
        if ((p === 'tool' || p === 'deitytool' || p === 'numa') && a === 'y') return false;
        // The dashboard scrolls: only its right edge hands a sideways drag over to the compass.
        if (p === 'dashboard' && !(a === 'x' && g.x0 > dims.current.w - 40)) return false;
        axis.current = a;
        return true;
      },
      onPanResponderMove: (_e, g) => {
        const { w, h } = dims.current;
        const p = posRef.current;
        const a = axis.current;
        if (!a) return;
        const d = a === 'y' ? g.dy : g.dx;
        const dest = neighbour(p, a, d);
        const lim = a === 'y' ? h : w;
        const dd = dest ? Math.max(-lim, Math.min(lim, d)) : 0;
        if (a === 'y') ty.setValue(-GRID[p][1] * h + dd);
        else tx.setValue(-GRID[p][0] * w + dd);
      },
      onPanResponderRelease: (_e, g) => {
        const p = posRef.current;
        const a = axis.current;
        axis.current = null;
        if (!a) return goRef.current(p);
        const d = a === 'y' ? g.dy : g.dx;
        const v = a === 'y' ? g.vy : g.vx;
        const dest = neighbour(p, a, d);
        goRef.current(dest && (Math.abs(d) > 80 || Math.abs(v) > 0.6) ? dest : p);
      },
      onPanResponderTerminate: () => {
        axis.current = null;
        goRef.current(posRef.current);
      },
    }),
  ).current;

  // ---- actions --------------------------------------------------------------
  const pickRow = (g: GearId) => {
    setSelOverride(g);
    setStepTool(null);
    toolIdRef.current = ORB_ROW_TOOL[g];
  };
  const openStepTool = (s: NowStep) => {
    if (s.designId) writeNav('tkne', { designId: s.designId });
    setStepTool({ id: s.tool, opts: s.toolOpts });
    toolIdRef.current = s.tool;
    goTo('tool');
  };
  // A deity's ▶ in the Pantheon slides straight right to its tool (panel right of
  // SELF); the tool's ◀ slides straight back to that deity's conversation, which
  // stays open on SELF.
  const openSeatToolFromDeity = (seatId: string) => {
    const seat = SEAT_BY_ID[seatId];
    if (!seat) return;
    const fromStep = step && step.tool === seat.shellTool;
    if (fromStep && step.designId) writeNav('tkne', { designId: step.designId });
    setDeityTool({ id: seat.shellTool, opts: fromStep ? step.toolOpts : undefined, name: seat.name });
    goTo('deitytool');
  };
  // ▶ does what swiping right on the selected row does (open its tool / session, or drill down).
  const goRight = () => {
    if (swipeRef.current) return swipeRef.current();
    toolIdRef.current = toolId;
    goTo('tool');
  };
  const openSeatTool = (seatId: string) => {
    const seat = SEAT_BY_ID[seatId];
    setDeityId(null);
    if (!seat) return;
    if (step && step.tool === seat.shellTool) return openStepTool(step);
    if (seat.shellTool === 'astrs') return onOpenAstra();
    onOpenTool(seat.shellTool, seat.gear);
  };

  // Screen awareness: tell the talking deities which screen of the canvas is showing.
  const lensTool = pos === 'tool' ? toolId : pos === 'deitytool' ? deityTool?.id : pos === 'numa' ? numaTool : undefined;
  const deitySheetName = deityId ? SEAT_BY_ID[deityId]?.name : undefined;
  const stepTitle = step?.title;
  useEffect(() => {
    const names: Record<CanvasPos, string> = {
      dashboard: 'DASHBOARD (now step, SHAPE sectors, Pantheon, NUMA, today)',
      compass: `LIFE (${lifeMode === 'compass' ? 'the compass' : 'the mountain'})`,
      orb: 'NOW (the orb: today\'s step and gears)',
      self: 'SELF (the Pantheon of deities)',
      tool: 'Tool',
      deitytool: 'Tool',
      numa: 'Tool',
    };
    const toolName = lensTool ? TOOL_NAMES[lensTool] ?? lensTool.toUpperCase() : undefined;
    setScreen({
      screen: toolName ? `Tool: ${toolName}` : names[pos],
      toolId: lensTool,
      toolName,
      deityName: lensTool ? getDeityAvatar(lensTool)?.deityName : undefined,
      gear: selectedGear,
      detail: [stepTitle ? `Current step: ${stepTitle}` : '', deitySheetName ? `${deitySheetName}'s card is open.` : ''].filter(Boolean).join(' ') || undefined,
      domId: `v10-${pos}`,
    });
  }, [pos, lifeMode, lensTool, selectedGear, stepTitle, deitySheetName]);

  const orbNode = (
    <NowOrb
      size={orbSize}
      userId={userId}
      today={today}
      sector={orbCtx.sector}
      level={orbLevel}
      onLevel={setOrbLevel}
      steps={steps}
      projects={projects}
      spine={spine}
      now={now}
      selectedGear={selectedGear}
      onPickRow={pickRow}
      onOpenTool={(id, opts) => {
        setStepTool({ id, opts });
        toolIdRef.current = id;
        goTo('tool');
      }}
      onOpenSession={onOpenSession}
      onRefresh={refresh}
      onTarget={(tg) => setOrbTarget((cur) => (cur && cur.tool === tg.tool && cur.opts?.initialTaskId === tg.opts?.initialTaskId && cur.opts?.initialProtocolId === tg.opts?.initialProtocolId ? cur : tg))}
      swipeRef={swipeRef}
      onAgent={(seatId) => setDeityId(seatId)}
    />
  );

  return (
    <OnboardingProvider eligible={tourEligible} navigate={tourNavigate} getScreen={() => posRef.current}>
    <View ref={rootClipRef} style={styles.root}>
      {/* ONE constant background, outside the panning canvas: it never moves or changes between screens. */}
      <NightBackdrop width={winW} height={winH} />
      <Animated.View style={{ width: winW * COLS, height: winH * 3, transform: [{ translateX: tx }, { translateY: ty }] }} {...pan.panHandlers}>
        {/* COMPASS screen (1,1): the Mountain world + the pinned compass. */}
        {/* Off-compass the zoomed, invisible mountain sits pinned over SELF/orb, so it only takes touches on its own screen. */}
        <Animated.View pointerEvents={pos === 'compass' ? 'auto' : 'none'} style={[styles.panel, { left: winW, top: winH, width: winW, height: winH }, flyMountain]} nativeID="v10-compass">
          <MountainBackdrop pitch={pitch} orbitFrame={orbit.frame} orbitFrames={ORBIT_FRAMES.length} anchorY={peakY} liftY={compassY} lift={lift} width={winW} height={winH} />
          <LifeView
            userId={userId}
            current={step}
            projects={projects}
            spine={spine}
            orbit={orbit}
            frameTop={frameTop}
            frameSize={frameSize}
            pillTop={pillTop}
            bottomY={bottomY}
            mode={lifeMode}
            onMode={setLifeMode}
            onOpenTool={(tool) => onOpenTool(tool)}
            onOrbCtx={handleOrbCtx}
            onNodePick={() => {
              tourBus.emit('orb:open');
              // An explicit pick / Explore: show the orb for the stage just chosen.
              adoptNext.current = true;
              setTimeout(() => {
                if (!adoptNext.current) return;
                adoptNext.current = false;
                setOrbLevel(orbCtxRef.current.stage);
              }, 150);
              goTo('orb');
            }}
            nodeText={(sector, stage) => {
              const node = sector && spine ? spine[sector] : null;
              if (stage === 'E') return node?.endeavour || null;
              if (stage === 'G') return goalTextOf(node) || null;
              if (stage === 'A') return (node ? effectiveAims(node)[0]?.label : null) || null;
              const list = sector ? projects[sector] ?? [] : [];
              if (stage === 'T') return (step && step.sector === sector ? step.title : null) || list[0]?.name || null;
              return (step && step.sector === sector ? step.title : null) || list[0]?.steps.find((x) => x.status !== 'C')?.text || null;
            }}
            frameLeft={frameLeft}
            anchorY={discAt === 'compass' ? compassY : peakY}
            bottomInset={insets.bottom}
            defaultGear={dayGear}
            world={world}
            spinTo={compassSpin}
            onSectorTitle={(s) => sectorDeity.openOverview(s)}
          />
          <FlowRibbon
            width={winW}
            height={winH}
            active={pos === 'compass' || pos === 'self' || pos === 'orb'}
            lines={[
              straight({ x: winW / 2, y: anchorY }, { x: winW / 2, y: headerH + navC }),
            ]}
          />
          <EdgeTabs top={headerH} bottom={insets.bottom} up={() => goTo('self')} down={() => goTo('orb')} left={() => goTo('dashboard')} right={() => {
            setNumaTool('numa');
            goTo('numa');
          }} labels={{ left: 'Open the dashboard', right: 'Open NUMA' }} upTargetId={pos === 'compass' ? 'mountain.up' : undefined} />
          <Animated.View pointerEvents="none" style={[{ position: 'absolute', left: 0, top: 0, width: winW, height: Math.round(winH * 0.3) }, mountainFeather]}>
            <LinearGradient colors={[t.night, `${t.night}00`]} style={StyleSheet.absoluteFill} />
          </Animated.View>
          <Animated.View pointerEvents="none" style={[{ position: 'absolute', left: 0, bottom: 0, width: winW, height: Math.round(winH * 0.3) }, mountainFeatherBottom]}>
            <LinearGradient colors={[`${t.night}00`, t.night]} style={StyleSheet.absoluteFill} />
          </Animated.View>
        </Animated.View>

        {/* ORB screen (1,2) = NOW: the orb, with the connector entering from the top. */}
        {/* At rest on the Mountain the orb screen sits invisibly over it (pinned), so it must not take touches. */}
        <Animated.View pointerEvents={pos === 'compass' || pos === 'self' ? 'none' : 'auto'} style={[styles.panel, { left: winW, top: winH * 2, width: winW, height: winH }, flyOrb]} nativeID="v10-orb">
          {/* Three currents out of the rim (behind the arrows) to the screen edges; they meet the neighbouring screens' ribbons/tabs exactly. */}
          <FlowRibbon
            width={winW}
            height={winH}
            active={pos === 'orb' || pos === 'compass' || pos === 'self'}
            lines={[
              straight({ x: winW / 2, y: orbTop + 4 }, { x: winW / 2, y: headerH + navC }),
              straight({ x: (winW - orbSize) / 2 + 4, y: orbCy }, { x: navC, y: orbCy }),
              straight({ x: (winW + orbSize) / 2 - 4, y: orbCy }, { x: winW - navC, y: orbCy }),
            ]}
          />
          <View
            style={[
              styles.orb,
              { top: orbTop, left: (winW - orbSize) / 2, width: orbSize, height: orbSize, borderRadius: orbSize / 2 },
            ]}
          >
            {orbNode}
          </View>
          <EdgeTabs top={headerH} bottom={insets.bottom} up={() => goTo('compass')} right={goRight} upTargetId={pos === 'orb' ? 'orb.up' : undefined} />
          <Animated.View pointerEvents="none" style={[{ position: 'absolute', left: 0, top: 0, width: winW, height: winH * 1.5 }, orbVeil]}>
            <LinearGradient colors={[t.night, t.night, `${t.night}00`]} locations={[0, 0.62, 1]} style={StyleSheet.absoluteFill} />
          </Animated.View>
        </Animated.View>

        {/* SELF panel (1,0): the PANTHEON, full bleed under the header — its
            open rooms, oculus and centre arch show the constant sky behind. The
            flow line runs behind the plate, so it only glows through the centre arch. */}
        {/* At rest off-SELF the hall sits invisibly over the Mountain (shrunk into the temple), so it must not take touches. */}
        <Animated.View pointerEvents={pos === 'self' ? 'auto' : 'none'} style={[styles.panel, { left: winW, top: 0, width: winW, height: winH }, flyHall]} nativeID="v10-self">
          <FlowRibbon
            width={winW}
            height={winH}
            active={pos === 'self' || pos === 'compass'}
            lines={[straight({ x: winW / 2, y: winH * 0.5 }, { x: winW / 2, y: winH - insets.bottom - navC })]}
          />
          <SelfView self={self} userId={userId} step={step} onOpenTool={openSeatToolFromDeity} onDown={() => goTo('compass')} bottomInset={insets.bottom} />
          <Animated.View pointerEvents="none" style={[{ position: 'absolute', left: 0, top: -winH * 0.5, width: winW, height: winH * 1.5 }, hallVeil]}>
            <LinearGradient colors={[`${t.night}00`, t.night, t.night]} locations={[0, 0.38, 1]} style={StyleSheet.absoluteFill} />
          </Animated.View>
        </Animated.View>

        {/* TOOL panel (2,2): the tool selected in the orb. */}
        <View style={[styles.panel, { left: winW * 2, top: winH * 2, width: winW, height: winH }]} nativeID="v10-tool">
          {toolMounted ? (
            <ToolScreen
              toolId={toolId}
              toolOpts={stepTool ? stepTool.opts : orbTarget?.opts}
              fallbackGear={(stepTool ? step?.gear ?? selectedGear : selectedGear) as Gear}
              width={winW}
              height={winH}
              top={headerH}
              bottom={insets.bottom}
              onBack={() => goTo('orb')}
              onSelectTool={(id) => {
                setStepTool({ id });
                toolIdRef.current = id;
                writeNav('v10canvas', { pos: 'tool', tool: id } satisfies SavedCanvas);
              }}
              onOpenTool={(id, opts) => {
                setStepTool({ id, opts });
                toolIdRef.current = id;
              }}
            />
          ) : null}
        </View>

        {/* DASHBOARD panel (0,1): left of the compass, where Home lands. Mounted on first visit. */}
        <View pointerEvents={pos === 'dashboard' ? 'auto' : 'none'} style={[styles.panel, { left: 0, top: winH, width: winW, height: winH }]} nativeID="v10-dashboard">
          {dashMounted ? (
            <DashboardView
              userId={userId}
              today={today}
              step={step}
              projects={projectsAreDemo ? {} : projects}
              self={self}
              width={winW}
              top={headerH}
              bottom={insets.bottom}
              onDoStep={openStepTool}
              onOpenNow={() => goTo('orb')}
              onOpenSector={(s) => {
                // The sector overview belongs to the compass: pan there and open it on that sector.
                goTo('compass');
                sectorDeity.openOverview(s);
              }}
              onOpenPantheon={() => goTo('self')}
              onOpenNuma={() => {
                setNumaTool('numa');
                goTo('numa');
              }}
              onOpenToday={() => onOpenTool('metis')}
              onOpenCalendar={() => onOpenTool('krnos')}
            />
          ) : null}
          <EdgeTabs top={headerH} bottom={insets.bottom} right={() => goTo('compass')} labels={{ right: 'Back to the compass' }} />
        </View>

        {/* NUMA panel (2,1): right of the compass, opened by the compass ▶. Mounted on first visit. */}
        <View style={[styles.panel, { left: winW * 2, top: winH, width: winW, height: winH }]} nativeID="v10-numa">
          {numaMounted ? (
            <ToolScreen
              toolId={numaTool}
              fallbackGear={selectedGear as Gear}
              width={winW}
              height={winH}
              top={headerH}
              bottom={insets.bottom}
              onBack={() => goTo('compass')}
              backLabel="Back to the compass"
              onSelectTool={setNumaTool}
              onOpenTool={(id, opts) => {
                setStepTool({ id, opts });
                toolIdRef.current = id;
                goTo('tool');
              }}
            />
          ) : null}
        </View>

        {/* DEITY TOOL panel (2,0): right of SELF — the tool a deity's ▶ opened. */}
        <View style={[styles.panel, { left: winW * 2, top: 0, width: winW, height: winH }]} nativeID="v10-deitytool">
          {deityTool ? (
            <ToolScreen
              toolId={deityTool.id}
              toolOpts={deityTool.opts}
              fallbackGear={(SEAT_BY_ID[deityTool.id]?.gear ?? selectedGear) as Gear}
              width={winW}
              height={winH}
              top={headerH}
              bottom={insets.bottom}
              onBack={() => goTo('self')}
              backLabel={`Back to ${deityTool.name}`}
              onSelectTool={(id) => setDeityTool((d) => (d ? { ...d, id, opts: undefined } : d))}
              onOpenTool={(id, opts) => setDeityTool((d) => (d ? { ...d, id, opts } : d))}
            />
          ) : null}
        </View>
      </Animated.View>

      {/* Blur haze over the whole Mountain ↔ Pantheon flight. */}
      <Animated.View pointerEvents="none" style={[StyleSheet.absoluteFill, { opacity: haze }]}>
        <BlurView intensity={70} tint="dark" style={StyleSheet.absoluteFill} />
      </Animated.View>
      <SectorDeityStage ctl={sectorDeity} />
      <SectorOverview ctl={sectorDeity} projects={projects} projectsAreDemo={projectsAreDemo} spine={spine} />
      {/* Global chrome stays put while the canvas moves under it. */}
      <View pointerEvents="box-none" style={[styles.top, { paddingTop: insets.top + 6 }]}>
        <Pressable onPress={onOpenDrawer} style={styles.iconBtn} hitSlop={8}>
          <Menu size={20} color={t.ivory} />
        </Pressable>
        <View pointerEvents="box-none" style={{ flexDirection: 'row', gap: 8 }}>
          {/* The sector deity belongs to the compass: its face only shows there. */}
          {pos === 'compass' ? <SectorDeityButton ctl={sectorDeity} /> : null}
          <QuickLinkButtons
            onHome={() => goTo('dashboard')}
            onTalk={() => toggleTalk('syqi', 'SyQi')}
            onCapture={onCapture}
            onOpenTool={(id) => onOpenTool(id)}
            onOpenSettings={onOpenSettings}
            buttonStyle={styles.iconBtn}
            hitSlop={8}
          />
        </View>
      </View>

      <DeitySheet
        seatId={deityId}
        seatView={deityId ? self.all.find((v) => v.seat.id === SEAT_BY_ID[deityId]?.id) : undefined}
        step={step}
        onClose={() => setDeityId(null)}
        onOpenTool={openSeatTool}
      />
      <TourLayer />
    </View>
    </OnboardingProvider>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    root: { flex: 1, backgroundColor: t.night, overflow: 'hidden' },
    orb: { position: 'absolute', overflow: 'visible' },
    panel: { position: 'absolute', overflow: 'hidden' },
    top: { position: 'absolute', left: 0, right: 0, top: 0, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, zIndex: 30 },
    iconBtn: { width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center', backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine },
  }),
);
